import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const FREE_PLACE_LIMIT = 10
export const FREE_GAME_LIMIT = 1
export const FREE_MODEL_IDS = new Set([
  'great-wall-of-china',
  'eiffel-tower',
  'taj-mahal',
  'sydney-opera-house',
  'christ-the-redeemer',
  'colosseum',
])

/** Billing stays fail-open until the production services are configured. */
export const BILLING_ENABLED = import.meta.env.VITE_BILLING_ENABLED === 'true'

export type EntitlementState = 'idle' | 'loading' | 'ready'

interface AccessState {
  unlockedPlaceIds: string[]
  classroomPlaceIds: string[]
  hasFullAccess: boolean
  entitlementState: EntitlementState
  freeQuizSessionsUsed: number
  freeWhereSessionsUsed: number
  unlockPlace: (id: string) => void
  grantClassroomPlaces: (ids: string[]) => void
  setEntitlement: (hasFullAccess: boolean) => void
  setEntitlementState: (state: EntitlementState) => void
  claimQuizSession: () => boolean
  claimWhereSession: () => boolean
}

function previouslyVisitedPlaces(): string[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const stored = JSON.parse(localStorage.getItem('kaleido-explorer-progress') ?? '{}')
    const ids = stored?.state?.landmarksVisited
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function hasStoredAdultSession() {
  if (typeof localStorage === 'undefined') return false
  try {
    return Object.keys(localStorage).some((key) => key.startsWith('sb-') && key.endsWith('-auth-token'))
  } catch {
    return false
  }
}

function storedClassroomPlaces(): string[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const ids = JSON.parse(sessionStorage.getItem('kaleido-classroom-places') ?? '[]')
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

export const useAccessStore = create<AccessState>()(
  persist(
    (set) => ({
      // Existing explorers keep every place they had already visited. This
      // avoids turning a newly introduced meter into a retroactive lock.
      unlockedPlaceIds: previouslyVisitedPlaces(),
      classroomPlaceIds: storedClassroomPlaces(),
      hasFullAccess: false,
      entitlementState: BILLING_ENABLED && hasStoredAdultSession() ? 'loading' : 'ready',
      freeQuizSessionsUsed: 0,
      freeWhereSessionsUsed: 0,
      unlockPlace: (id) => set((state) =>
        state.unlockedPlaceIds.includes(id)
          ? state
          : { unlockedPlaceIds: [...state.unlockedPlaceIds, id] },
      ),
      grantClassroomPlaces: (ids) => {
        const classroomPlaceIds = [...new Set(ids)]
        sessionStorage.setItem('kaleido-classroom-places', JSON.stringify(classroomPlaceIds))
        set({ classroomPlaceIds })
      },
      setEntitlement: (hasFullAccess) => set({ hasFullAccess, entitlementState: 'ready' }),
      setEntitlementState: (entitlementState) => set({ entitlementState }),
      claimQuizSession: () => {
        const state = useAccessStore.getState()
        if (!BILLING_ENABLED || state.hasFullAccess) return true
        if (state.freeQuizSessionsUsed >= FREE_GAME_LIMIT) return false
        set({ freeQuizSessionsUsed: state.freeQuizSessionsUsed + 1 })
        return true
      },
      claimWhereSession: () => {
        const state = useAccessStore.getState()
        if (!BILLING_ENABLED || state.hasFullAccess) return true
        if (state.freeWhereSessionsUsed >= FREE_GAME_LIMIT) return false
        set({ freeWhereSessionsUsed: state.freeWhereSessionsUsed + 1 })
        return true
      },
    }),
    {
      name: 'kaleido-access',
      partialize: ({ unlockedPlaceIds, freeQuizSessionsUsed, freeWhereSessionsUsed }) => ({
        unlockedPlaceIds,
        freeQuizSessionsUsed,
        freeWhereSessionsUsed,
      }),
    },
  ),
)

export function canAccessPlace(id: string): boolean {
  if (!BILLING_ENABLED) return true
  const state = useAccessStore.getState()
  return state.hasFullAccess
    || state.unlockedPlaceIds.includes(id)
    || state.unlockedPlaceIds.length < FREE_PLACE_LIMIT
}

export function canAccessModel(id: string): boolean {
  if (!BILLING_ENABLED) return true
  return useAccessStore.getState().hasFullAccess || FREE_MODEL_IDS.has(id)
}
